import * as THREE from 'three';

import type { EnvironmentThemeDefinition } from './themes';

export interface CoasterAppearanceDefinition {
  readonly trackColor: number;
  readonly tieColor: number;
  readonly supportColor: number;
  readonly trainColor: number;
  readonly stationColor: number;
  readonly stationRoofColor: number;
}

export const DEFAULT_COASTER_APPEARANCE: CoasterAppearanceDefinition = Object.freeze({
  trackColor: 0x2e5eaa,
  tieColor: 0x283744,
  supportColor: 0x4d5965,
  trainColor: 0xc84e3f,
  stationColor: 0xe9dfcd,
  stationRoofColor: 0x385768,
});

export interface EnvironmentMaterialSet {
  readonly plotTop: THREE.MeshStandardMaterial;
  readonly plotEdge: THREE.MeshStandardMaterial;
  readonly path: THREE.MeshStandardMaterial;
  readonly foliage: THREE.MeshStandardMaterial;
  readonly foliageAccent: THREE.MeshStandardMaterial;
  readonly trunk: THREE.MeshStandardMaterial;
  readonly stone: THREE.MeshStandardMaterial;
  readonly practical: THREE.MeshStandardMaterial;
}

export interface CoasterMaterialSet {
  readonly track: THREE.MeshStandardMaterial;
  readonly tie: THREE.MeshStandardMaterial;
  readonly support: THREE.MeshStandardMaterial;
  readonly train: THREE.MeshStandardMaterial;
  readonly station: THREE.MeshStandardMaterial;
  readonly stationRoof: THREE.MeshStandardMaterial;
  readonly chain: THREE.MeshPhysicalMaterial;
  readonly launch: THREE.MeshStandardMaterial;
  readonly brake: THREE.MeshPhysicalMaterial;
}

export type ProductionSurfaceId =
  | 'painted_track'
  | 'painted_support'
  | 'train_shell'
  | 'mechanical_steel'
  | 'chassis'
  | 'rubber'
  | 'seat_pad'
  | 'safety_accent'
  | 'station_floor'
  | 'station_structure'
  | 'wood'
  | 'stone'
  | 'foliage'
  | 'practical';

export interface ProductionSurfaceProfile {
  readonly roughness: number;
  readonly metalness: number;
  readonly clearcoat: number;
  readonly clearcoatRoughness: number;
  readonly envMapIntensity: number;
  readonly emissiveIntensity?: number;
}

export const PRODUCTION_SURFACES: Readonly<Record<ProductionSurfaceId, ProductionSurfaceProfile>> =
  Object.freeze({
    painted_track: Object.freeze({
      roughness: 0.32,
      metalness: 0,
      clearcoat: 0.28,
      clearcoatRoughness: 0.3,
      envMapIntensity: 0.48,
    }),
    painted_support: Object.freeze({
      roughness: 0.58,
      metalness: 0,
      clearcoat: 0.08,
      clearcoatRoughness: 0.5,
      envMapIntensity: 0.3,
    }),
    train_shell: Object.freeze({
      roughness: 0.28,
      metalness: 0,
      clearcoat: 0.62,
      clearcoatRoughness: 0.22,
      envMapIntensity: 0.55,
    }),
    mechanical_steel: Object.freeze({
      roughness: 0.36,
      metalness: 0.76,
      clearcoat: 0.08,
      clearcoatRoughness: 0.34,
      envMapIntensity: 0.58,
    }),
    chassis: Object.freeze({
      roughness: 0.48,
      metalness: 0.34,
      clearcoat: 0.06,
      clearcoatRoughness: 0.48,
      envMapIntensity: 0.38,
    }),
    rubber: Object.freeze({
      roughness: 0.92,
      metalness: 0,
      clearcoat: 0,
      clearcoatRoughness: 1,
      envMapIntensity: 0.12,
    }),
    seat_pad: Object.freeze({
      roughness: 0.78,
      metalness: 0,
      clearcoat: 0.04,
      clearcoatRoughness: 0.7,
      envMapIntensity: 0.22,
    }),
    safety_accent: Object.freeze({
      roughness: 0.34,
      metalness: 0,
      clearcoat: 0.36,
      clearcoatRoughness: 0.3,
      envMapIntensity: 0.44,
    }),
    station_floor: Object.freeze({
      roughness: 0.86,
      metalness: 0,
      clearcoat: 0,
      clearcoatRoughness: 1,
      envMapIntensity: 0.2,
    }),
    station_structure: Object.freeze({
      roughness: 0.5,
      metalness: 0,
      clearcoat: 0.2,
      clearcoatRoughness: 0.42,
      envMapIntensity: 0.35,
    }),
    wood: Object.freeze({
      roughness: 0.76,
      metalness: 0,
      clearcoat: 0.04,
      clearcoatRoughness: 0.68,
      envMapIntensity: 0.2,
    }),
    stone: Object.freeze({
      roughness: 0.88,
      metalness: 0,
      clearcoat: 0,
      clearcoatRoughness: 1,
      envMapIntensity: 0.16,
    }),
    foliage: Object.freeze({
      roughness: 0.82,
      metalness: 0,
      clearcoat: 0.02,
      clearcoatRoughness: 0.78,
      envMapIntensity: 0.18,
    }),
    practical: Object.freeze({
      roughness: 0.38,
      metalness: 0,
      clearcoat: 0.16,
      clearcoatRoughness: 0.36,
      envMapIntensity: 0.28,
      emissiveIntensity: 0.3,
    }),
  });

const MATERIAL_SURFACE_BY_NAME: Readonly<Record<string, ProductionSurfaceId>> = Object.freeze({
  M_Coaster_CreatorPrimary: 'train_shell',
  M_Coaster_PowdercoatDark: 'chassis',
  M_Coaster_Seat: 'seat_pad',
  M_Coaster_SafetyAccent: 'safety_accent',
  M_Station_CreatorSurface: 'station_floor',
  M_Station_Roof: 'station_structure',
  M_Park_Wood: 'wood',
  M_Park_Foliage: 'foliage',
  M_Park_FoliageAccent: 'foliage',
  M_Park_Practical: 'practical',
  M_Park_Structure: 'stone',
});

export function applySurfaceProfile(
  material: THREE.MeshStandardMaterial,
  surface: ProductionSurfaceId,
): void {
  const profile = PRODUCTION_SURFACES[surface];
  material.roughness = profile.roughness;
  material.metalness = profile.metalness;
  material.envMapIntensity = profile.envMapIntensity;
  if (material instanceof THREE.MeshPhysicalMaterial) {
    material.clearcoat = profile.clearcoat;
    material.clearcoatRoughness = profile.clearcoatRoughness;
  }
  if (profile.emissiveIntensity !== undefined)
    material.emissiveIntensity = profile.emissiveIntensity;
  material.needsUpdate = true;
}

export function applyNamedProductionSurface(material: THREE.Material): ProductionSurfaceId | null {
  const surface = MATERIAL_SURFACE_BY_NAME[material.name];
  if (!surface || !(material instanceof THREE.MeshStandardMaterial)) return null;
  applySurfaceProfile(material, surface);
  return surface;
}

function standard(color: number, roughness: number, metalness = 0): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness,
    metalness,
    envMapIntensity: 0.42,
  });
}

function physical(
  color: number,
  roughness: number,
  metalness: number,
  clearcoat: number,
): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color,
    roughness,
    metalness,
    clearcoat,
    clearcoatRoughness: 0.28,
    envMapIntensity: 0.42,
  });
}

export function createEnvironmentMaterials(
  theme: EnvironmentThemeDefinition,
): EnvironmentMaterialSet {
  const materials = {
    plotTop: standard(theme.colors.plotTop, theme.surface.groundRoughness),
    plotEdge: standard(theme.colors.plotEdge, 0.82),
    path: standard(theme.colors.path, theme.surface.stoneRoughness),
    foliage: standard(theme.colors.foliage, PRODUCTION_SURFACES.foliage.roughness),
    foliageAccent: standard(theme.colors.foliageAccent, PRODUCTION_SURFACES.foliage.roughness),
    trunk: standard(theme.colors.trunk, PRODUCTION_SURFACES.wood.roughness),
    stone: standard(theme.colors.stone, PRODUCTION_SURFACES.stone.roughness),
    practical: new THREE.MeshStandardMaterial({
      color: theme.colors.practical,
      emissive: theme.colors.practical,
      emissiveIntensity: theme.id === 'afterglow' ? 2.2 : 0.3,
      roughness: 0.4,
      envMapIntensity: 0.35,
    }),
  };
  applySurfaceProfile(materials.foliage, 'foliage');
  applySurfaceProfile(materials.foliageAccent, 'foliage');
  applySurfaceProfile(materials.trunk, 'wood');
  applySurfaceProfile(materials.stone, 'stone');
  applySurfaceProfile(materials.practical, 'practical');
  materials.practical.emissiveIntensity = theme.id === 'afterglow' ? 2.2 : 0.3;
  return materials;
}

export function createCoasterMaterials(
  appearance: CoasterAppearanceDefinition = DEFAULT_COASTER_APPEARANCE,
): CoasterMaterialSet {
  const materials = {
    track: physical(appearance.trackColor, 0.32, 0, 0.28),
    tie: physical(appearance.tieColor, 0.36, 0.76, 0.08),
    support: physical(appearance.supportColor, 0.58, 0, 0.08),
    train: physical(appearance.trainColor, 0.28, 0, 0.62),
    station: standard(appearance.stationColor, 0.86),
    stationRoof: physical(appearance.stationRoofColor, 0.5, 0, 0.2),
    chain: new THREE.MeshPhysicalMaterial({
      color: 0xe1b84b,
      emissive: 0x5a3b05,
      emissiveIntensity: 0.28,
      roughness: 0.3,
      metalness: 0.76,
      clearcoat: 0.18,
      clearcoatRoughness: 0.28,
      envMapIntensity: 0.48,
    }),
    launch: new THREE.MeshStandardMaterial({
      color: 0x62def2,
      emissive: 0x38bcd8,
      emissiveIntensity: 0.75,
      roughness: 0.32,
      metalness: 0.5,
      envMapIntensity: 0.42,
    }),
    brake: new THREE.MeshPhysicalMaterial({
      color: 0xd96f35,
      emissive: 0x4a1607,
      emissiveIntensity: 0.18,
      roughness: 0.36,
      metalness: 0.58,
      clearcoat: 0.16,
      clearcoatRoughness: 0.3,
      envMapIntensity: 0.46,
    }),
  } satisfies CoasterMaterialSet;
  applySurfaceProfile(materials.track, 'painted_track');
  applySurfaceProfile(materials.tie, 'mechanical_steel');
  applySurfaceProfile(materials.support, 'painted_support');
  applySurfaceProfile(materials.train, 'train_shell');
  applySurfaceProfile(materials.station, 'station_floor');
  applySurfaceProfile(materials.stationRoof, 'station_structure');
  applySurfaceProfile(materials.chain, 'mechanical_steel');
  applySurfaceProfile(materials.launch, 'mechanical_steel');
  applySurfaceProfile(materials.brake, 'mechanical_steel');
  return materials;
}

export function disposeMaterialSet(materials: object): void {
  for (const material of Object.values(materials) as THREE.Material[]) material.dispose();
}
